import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Phone, ShieldAlert } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import BotCheck, { useBotCheck } from "./BotCheck";

const FALLBACK_REASONS = [
  { slug: "minor", label: "Person may be under 18" },
  { slug: "coercion", label: "Coercion, control or exploitation" },
  { slug: "stolen_photos", label: "Stolen or fake photos" },
  { slug: "scam", label: "Scam or deposit request" },
  { slug: "explicit", label: "Explicit content" },
  { slug: "other", label: "Something else" },
];

export function ReportForm({ profileSlug, storyId, onDone, testPrefix = "report-form" }) {
  const { data: loc } = useQuery({ queryKey: ["locations"], queryFn: async () => (await api.get("/public/locations")).data, staleTime: 600000 });
  const reasons = loc?.report_reasons || FALLBACK_REASONS;
  const bot = useBotCheck();
  const [target, setTarget] = useState(profileSlug || "");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!reason) return setError("Choose a reason for your report");
    if (!bot.answer) return setError("Please answer the quick human check");
    setBusy(true);
    try {
      const { data } = await api.post("/public/reports", {
        target_type: storyId ? "story" : target ? "profile" : "general",
        profile_slug: target || null,
        story_id: storyId || null,
        reason,
        details,
        reporter_contact: contact,
        ...bot.payload(),
      });
      setDone(data.reference);
      onDone && onDone(data.reference);
    } catch (err) {
      setError(errMsg(err));
      bot.refresh();
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="py-6 text-center" data-testid={`${testPrefix}-success`}>
        <CheckCircle2 className="mx-auto h-10 w-10 text-lux-success" aria-hidden="true" />
        <h3 className="mt-4 font-serif text-2xl">Thank you. Your report has been received.</h3>
        <p className="mt-2 text-sm text-lux-muted">
          Reference <span className="font-mono text-lux-silverBright" data-testid={`${testPrefix}-reference`}>{done}</span>. Reports about possible minors or coercion go straight to our compliance team.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid={testPrefix} noValidate>
      <div className="flex gap-3 rounded-[10px] border border-lux-warning/30 bg-lux-warning/5 p-3 text-sm text-lux-text/90">
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-lux-warning" aria-hidden="true" />
        <p>
          If someone is in immediate danger, call <a href="tel:999" className="lux-link font-medium">999</a>. For exploitation concerns you can also call the Modern Slavery &amp; Exploitation Helpline on{" "}
          <a href="tel:08000121700" className="lux-link font-medium whitespace-nowrap"><Phone className="inline h-3 w-3" /> 08000 121 700</a>.
        </p>
      </div>
      {!profileSlug && !storyId && (
        <div>
          <label htmlFor={`${testPrefix}-target`} className="lux-label">Profile link or name (optional)</label>
          <input id={`${testPrefix}-target`} className="lux-input mt-2" placeholder="e.g. https://.../profile/aurelia" value={target}
            onChange={(e) => setTarget(e.target.value)} data-testid={`${testPrefix}-target-input`} />
        </div>
      )}
      <fieldset>
        <legend className="lux-label">What is the concern?</legend>
        <div className="mt-2 grid gap-2 sm:grid-cols-2" role="radiogroup">
          {reasons.map((r) => (
            <label key={r.slug}
              className={`flex cursor-pointer items-center gap-3 rounded-[10px] border px-3.5 py-3 text-sm transition-colors ${reason === r.slug ? "border-lux-silver bg-white/5 text-lux-text" : "border-lux-border text-lux-text/85 hover:border-[#3a3a40]"}`}
              data-testid={`${testPrefix}-reason-${r.slug}`}>
              <input type="radio" name="reason" value={r.slug} checked={reason === r.slug} onChange={() => setReason(r.slug)} className="accent-[#C0C0C8]" />
              {r.label}
            </label>
          ))}
        </div>
      </fieldset>
      <div>
        <label htmlFor={`${testPrefix}-details`} className="lux-label">Details {reason === "other" ? "(required)" : "(optional)"}</label>
        <textarea id={`${testPrefix}-details`} rows={4} maxLength={2000} className="lux-input mt-2" value={details}
          onChange={(e) => setDetails(e.target.value)} placeholder="What did you notice? Please don't include anyone's private information."
          data-testid={`${testPrefix}-message-textarea`} />
        <p className="mt-1 text-right text-xs text-lux-muted">{details.length}/2000</p>
      </div>
      <div>
        <label htmlFor={`${testPrefix}-contact`} className="lux-label">Your email or phone (optional)</label>
        <input id={`${testPrefix}-contact`} className="lux-input mt-2" value={contact} onChange={(e) => setContact(e.target.value)}
          placeholder="Only if you'd like us to follow up" data-testid={`${testPrefix}-email-input`} />
        <p className="mt-1 text-xs text-lux-muted">Encrypted and visible only to our compliance team.</p>
      </div>
      <BotCheck state={bot} testPrefix={testPrefix} />
      {error && <p role="alert" className="text-sm text-lux-error" data-testid={`${testPrefix}-error`}>{error}</p>}
      <button type="submit" disabled={busy} className="lux-btn lux-btn-primary w-full sm:w-auto" data-testid={`${testPrefix}-submit-button`}>
        {busy ? "Sending..." : "Send report"}
      </button>
      <p className="text-xs text-lux-muted">You don't need an account. Reports are reviewed by trained staff; we never tell advertisers who reported them.</p>
    </form>
  );
}

export function ReportDialog({ profileSlug, storyId, trigger, open, onOpenChange, title = "Report a concern" }) {
  const [inner, setInner] = useState(false);
  const isOpen = open !== undefined ? open : inner;
  const setOpen = onOpenChange || setInner;
  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent className="max-h-[92vh] overflow-y-auto border-lux-border bg-lux-surface sm:max-w-xl" data-testid="report-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-medium">{title}</DialogTitle>
          <DialogDescription className="text-lux-muted">Anonymous. Every report creates a moderation case.</DialogDescription>
        </DialogHeader>
        <ReportForm profileSlug={profileSlug} storyId={storyId} testPrefix={storyId ? "story-report-form" : "report-form"} />
      </DialogContent>
    </Dialog>
  );
}
